import { useStableCallback } from "@bezoned/shared/hooks/use-stable-callback.js";
import { useQuery } from "@tanstack/react-query";

import { useTeamsNavigation } from "../../hooks/use-teams-navigation.js";
import { channelTabList } from "../../services/api.service.js";
import { useLayoutStore } from "../../store/layout.store.js";
import { recordErrorInSentry } from "../../utils/init-sentry.js";
import { useAuth } from "../auth/use-auth.js";
import { ContextMenuContent } from "./context-menu-content.jsx";

interface TableContextMenuProps {
  onNavigationCompleted: () => void;
  channelUuid: string;
  displayName: string | null;
  tableId: string;
}

export const TableContextMenu = ({
  channelUuid,
  displayName,
  onNavigationCompleted,
  tableId,
}: TableContextMenuProps) => {
  const { setModalVariant } = useLayoutStore();
  const auth = useAuth();
  const { data, error, isFetching, isLoading } = useQuery({
    queryFn: () => channelTabList({ channelUuid }),
    queryKey: ["table-context-menu", "getChannelWithTabs", channelUuid],
    refetchOnMount: "always",
    staleTime: 0,
  });
  const isPending = isLoading || isFetching;

  const { navigateToChannel } = useTeamsNavigation({
    onCompleted: onNavigationCompleted,
  });

  const handleAddNewBoard = useStableCallback(() => {
    setModalVariant("new_board", {
      onNext: () => {
        if (!data) {
          recordErrorInSentry(
            new Error(
              "Edit navigation happened before we received MS Channel ID from API"
            ),
            {
              extra: {
                channelUuid,
              },
              user: {
                email: auth.user.email,
                id: auth.user.id,
              },
            }
          );

          return;
        }

        if (data.msChannelId) {
          navigateToChannel(data.msChannelId, displayName ?? "conversations");
        }
      },
    });
  });

  const hasContentError = !isPending && (error || !data);
  const handleTableSettings73 = useStableCallback(() => {
    if (!data) {
      return;
    }

    setModalVariant("table_settings", {
      fluidTableId: tableId,
      presetTableId: data.table.presetTableId,
      tableName: displayName,
    });
  });

  return (
    <ContextMenuContent
      channelUuid={hasContentError ? null : channelUuid}
      displayName={displayName}
      loading={isPending}
      msChannelId={data?.msChannelId ?? null}
      onAddNewBoard={handleAddNewBoard}
      onNavigationCompleted={onNavigationCompleted}
      onTableSettings={handleTableSettings73}
      tabs={data?.tabs ?? null}
      variant="table"
    />
  );
};
